import { useMemo } from 'react';

import type {
  DuplicateMap,
  NormalizedModuleMap,
  SelectionKey,
} from '../types/merge-customization';
import { isIdField, isTechnicalValue } from '../utils/fieldFilters';

/**
 * Build a canonical identity string for an item using its data values.
 *
 * Works for any module type by extracting meaningful string values,
 * normalizing them, and sorting for stable comparison regardless of
 * key ordering in the source data.
 */
function getCanonicalIdentity(
  moduleName: string,
  data: Record<string, unknown>,
): string | null {
  const parts: string[] = [];

  for (const [key, val] of Object.entries(data)) {
    if (isIdField(key)) continue;
    if (typeof val === 'string' && val.trim()) {
      if (isTechnicalValue(val)) continue;
      parts.push(`${key}=${val.toLowerCase().trim()}`);
    }
  }

  if (parts.length === 0) return null;

  // Sort for stable comparison regardless of key ordering
  parts.sort();
  return `${moduleName}::${parts.join('::')}`;
}

/**
 * Detects duplicate items across patients within each module.
 *
 * Returns a DuplicateMap where each entry maps a canonical identity string
 * to the list of selectionKeys that share it. Only entries with 2+ keys
 * (i.e. actual duplicates across different patients) are included.
 *
 * Also returns a Set of selectionKeys that are duplicates for quick lookup.
 */
export function useDuplicateDetection(modules: NormalizedModuleMap): {
  duplicateMap: DuplicateMap;
  isDuplicate: (key: SelectionKey) => boolean;
  getDuplicatesOf: (key: SelectionKey) => SelectionKey[];
} {
  const { duplicateMap, keyToIdentity } = useMemo(() => {
    const identityGroups = new Map<string, SelectionKey[]>();
    const keyIdentityMap = new Map<SelectionKey, string>();

    for (const mod of Object.values(modules)) {
      for (const item of mod.items) {
        const identity = getCanonicalIdentity(item.moduleName, item.data);
        if (!identity) continue;

        keyIdentityMap.set(item.selectionKey, identity);

        const existing = identityGroups.get(identity);
        if (existing) {
          existing.push(item.selectionKey);
        } else {
          identityGroups.set(identity, [item.selectionKey]);
        }
      }
    }

    // Filter to only entries with items from multiple patients
    const dupes: DuplicateMap = new Map();
    for (const [identity, keys] of identityGroups) {
      if (keys.length < 2) continue;
      // Check that keys come from at least 2 different patients
      const patients = new Set<string>();
      for (const key of keys) {
        patients.add(key.split('::')[0]);
      }
      if (patients.size >= 2) {
        dupes.set(identity, keys);
      }
    }

    return { duplicateMap: dupes, keyToIdentity: keyIdentityMap };
  }, [modules]);

  const isDuplicate = useMemo(() => {
    const dupeKeys = new Set<SelectionKey>();
    for (const keys of duplicateMap.values()) {
      for (const key of keys) {
        dupeKeys.add(key);
      }
    }
    return (key: SelectionKey) => dupeKeys.has(key);
  }, [duplicateMap]);

  const getDuplicatesOf = useMemo(() => {
    return (key: SelectionKey): SelectionKey[] => {
      const identity = keyToIdentity.get(key);
      if (!identity) return [];
      const keys = duplicateMap.get(identity);
      if (!keys) return [];
      return keys.filter(k => k !== key);
    };
  }, [duplicateMap, keyToIdentity]);

  return { duplicateMap, isDuplicate, getDuplicatesOf };
}
